import { Head, Link, usePage } from "@inertiajs/react"
import { AppLayout } from "@/layouts/app-layout"
import { PageHeader } from "@/hooks/use-page-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardFooter } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Pagination } from "@/components/pagination"
import { EmptyState } from "@/components/empty-state"
import { useDebouncedSearch } from "@/hooks/use-debounced-search"
import { Plus, Search, Contact } from "lucide-react"
import type { ProjectContact, PagyMetadata, SharedProps } from "@/types"

type Props = {
  contacts: ProjectContact[]
  filters: Record<string, string>
  pagy: PagyMetadata
}

function ContactsIndex({ contacts, filters, pagy }: Props) {
  const { permissions } = usePage<SharedProps>().props
  const canCreate = permissions?.contacts.create

  const [search, setSearch, searchRef] = useDebouncedSearch(
    filters.first_name_or_last_name_or_email_cont || "",
    "/contacts",
    "first_name_or_last_name_or_email_cont",
  )

  return (
    <>
      <Head title="Contacts" />
      <PageHeader title="Contacts" />
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="relative max-w-sm flex-1">
            <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
            <Input
              ref={searchRef}
              placeholder="Rechercher par nom ou email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="secondary">
              {pagy.count} résultat{pagy.count > 1 ? "s" : ""}
            </Badge>
            {canCreate && (
              <Button asChild>
                <Link href="/contacts/new">
                  <Plus className="size-4" />
                  Nouveau contact
                </Link>
              </Button>
            )}
          </div>
        </div>

        <Card>
          <CardContent className="px-0">
            {contacts.length === 0 ? (
              <EmptyState
                icon={Contact}
                title="Aucun contact trouvé"
                description="Essayez de modifier vos critères de recherche."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Téléphone</TableHead>
                    <TableHead>Rôle</TableHead>
                    <TableHead>Organisation</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.map((contact) => (
                    <TableRow key={contact.id}>
                      <TableCell className="font-medium">
                        <Link href={`/contacts/${contact.id}`} className="hover:underline">
                          {contact.display_name}
                        </Link>
                      </TableCell>
                      <TableCell>{contact.email || "—"}</TableCell>
                      <TableCell>{contact.phone_number || "—"}</TableCell>
                      <TableCell>{contact.role || "—"}</TableCell>
                      <TableCell>{contact.organisation?.name || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
          <CardFooter>
            <Pagination pagy={pagy} baseUrl="/contacts" filters={filters} className="w-full p-0" />
          </CardFooter>
        </Card>
      </div>
    </>
  )
}

ContactsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>

export default ContactsIndex
